import axios from 'axios';
import {
  SessionState,
  SessionStateResponse,
  CreateSessionRequest,
  SendEmailRequest,
  SendSlackMessageRequest,
  SessionResult,
  MakeDecisionRequest,
  ScenarioDefinition
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// EdgeHub token management
let edgeHubToken: string | null = null;

export function setEdgeHubToken(token: string | null) {
  edgeHubToken = token;
}

// Request interceptor — attach EdgeHub JWT if present
apiClient.interceptors.request.use(
  (config) => {
    if (edgeHubToken) {
      config.headers['Authorization'] = `Bearer ${edgeHubToken}`;
    }
    const functionsKey = import.meta.env.VITE_FUNCTIONS_KEY;
    if (functionsKey) {
      config.headers['x-functions-key'] = functionsKey;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);

    if (error.response?.status === 401) {
      // If this is an EdgeHub session and token expired, don't redirect to login
      if (edgeHubToken) {
        console.warn('EdgeHub token expired or invalid');
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Parse EdgeHub launch parameters from URL query string.
 * Called once on app init. Strips params from URL after reading.
 */
export function parseEdgeHubLaunchParams(): { token: string; scenario: string } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const scenario = params.get('scenario');

  if (token) {
    // Strip sensitive params from URL without triggering navigation
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('scenario');
    window.history.replaceState({}, '', url.pathname + url.search);

    return { token, scenario: scenario || '' };
  }

  return null;
}

/**
 * Decode basic JWT claims without validation (validation happens server-side).
 */
export function decodeJwtClaims(token: string): { userId: string; name: string; email: string } | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return {
      userId: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || decoded.sub || '',
      name: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || decoded.name || '',
      email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded.email || '',
    };
  } catch {
    return null;
  }
}

export const api = {
  // Session Management
  createSession: async (request: CreateSessionRequest): Promise<SessionState> => {
    const response = await apiClient.post<SessionState>('/sessions', request);
    return response.data;
  },

  startSession: async (sessionId: string): Promise<SessionState> => {
    const response = await apiClient.post<SessionState>(`/sessions/${sessionId}/start`);
    return response.data;
  },

  getSession: async (sessionId: string): Promise<SessionState> => {
    const response = await apiClient.get<SessionState>(`/sessions/${sessionId}`);
    return response.data;
  },

  getSessionState: async (sessionId: string): Promise<SessionStateResponse> => {
    const response = await apiClient.get<SessionStateResponse>(`/sessions/${sessionId}/state`);
    return response.data;
  },

  // Email Functions
  sendEmail: async (sessionId: string, emailRequest: SendEmailRequest): Promise<{ eventId: string; message: string }> => {
    const response = await apiClient.post<{ eventId: string; message: string }>(`/sessions/${sessionId}/email`, emailRequest);
    return response.data;
  },

  // Email Actions
  markEmailRead: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/read`);
    return response.data;
  },

  archiveEmail: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/archive`);
    return response.data;
  },

  flagEmail: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/flag`);
    return response.data;
  },

  // Slack Functions
  sendSlackMessage: async (sessionId: string, request: SendSlackMessageRequest): Promise<{ eventId: string; message: string }> => {
    const response = await apiClient.post<{ eventId: string; message: string }>(`/sessions/${sessionId}/slack`, request);
    return response.data;
  },

  // Phone Functions
  listenVoicemail: async (sessionId: string, voicemailId: string): Promise<{ success: boolean; voicemailId: string }> => {
    const response = await apiClient.post<{ success: boolean; voicemailId: string }>(`/sessions/${sessionId}/voicemail/${voicemailId}/listen`);
    return response.data;
  },

  // Scenario Functions
  getScenario: async (scenarioId: string): Promise<ScenarioDefinition> => {
    const response = await apiClient.get<ScenarioDefinition>(`/scenarios/${scenarioId}`);
    return response.data;
  },

  // Decision Functions
  makeDecision: async (sessionId: string, request: MakeDecisionRequest): Promise<{ decisionId: string; optionId: string; feedback: string }> => {
    const response = await apiClient.post<{ decisionId: string; optionId: string; feedback: string }>(`/sessions/${sessionId}/decisions`, request);
    return response.data;
  },

  // Session Control
  completeSession: async (sessionId: string): Promise<{ message: string; sessionId: string }> => {
    const response = await apiClient.post<{ message: string; sessionId: string }>(`/sessions/${sessionId}/complete`);
    return response.data;
  },

  // Results
  getSessionResults: async (sessionId: string): Promise<SessionResult> => {
    const response = await apiClient.get<SessionResult>(`/sessions/${sessionId}/results`);
    return response.data;
  },

  // Polling for updates (simple approach for MVP)
  getUpdates: async (sessionId: string, since?: string): Promise<SessionStateResponse> => {
    const params = since ? { since } : {};
    const response = await apiClient.get<SessionStateResponse>(`/sessions/${sessionId}/updates`, { params });
    return response.data;
  },
};

export default api;
